import { isNil } from 'lodash';
import { Observable, Subject, filter, map } from 'rxjs';

interface CacheMessage<T = unknown> {
  key: string;
  data: T;
}

interface CacheClearOptions {
  exclude?: string[];
}

class ObservableCache {
  private subject$: Subject<CacheMessage> = new Subject();
  private inMemory: Record<string, unknown> = {};

  constructor(private cache: LocalForage) {
    this.initialize();
  }

  protected initialize() {
    // subscribe to changes to keep in memory cache up to date
    this.subject$.subscribe((message) => {
      if (isNil(message.data)) {
        delete this.inMemory[message.key];
      } else {
        this.inMemory[message.key] = message.data;
      }
    });
  }

  public async restore(): Promise<void> {
    // hydrate the in memory cache
    await this.cache.iterate((value, key) => {
      this.inMemory[key] = value;
    });
  }

  public async setItem<T>(key: string, value: T): Promise<T> {
    const result = await this.cache.setItem(key, value);
    this.subject$.next({ key, data: value });
    return result;
  }

  public async getItem<T>(key: string): Promise<T | null> {
    const result = await this.cache.getItem<T>(key);
    return result;
  }

  public getItemSync<T>(key: string): T | null {
    return this.inMemory[key] as T;
  }

  public async deleteItem(key: string): Promise<void> {
    const result = await this.cache.removeItem(key);
    this.subject$.next({ key, data: null });
    return result;
  }

  public async clear(options?: CacheClearOptions): Promise<void> {
    if (options?.exclude) {
      await this.cache.iterate((_value, key) => {
        if (!options.exclude?.includes(key)) {
          this.cache.removeItem(key);
          delete this.inMemory[key];
        }
      });
    } else {
      await this.cache.clear();
    }
  }

  public observe<T>(key: string): Observable<T> {
    return this.subject$.pipe(
      filter((m) => m.key === key),
      map((m) => m.data as T)
    );
  }

  public async getItemFromList<T>(
    key: string,
    filter: (entry: T) => boolean
  ): Promise<T | null> {
    const existing = await this.cache.getItem<T[]>(key);

    if (existing == null) {
      return null;
    }

    return existing.find(filter) ?? null;
  }

  public async addItemToList<T>(
    key: string,
    item: T,
    isEqual: (itemToAdd: T, entry: T) => boolean
  ): Promise<T[]> {
    let existing = await this.cache.getItem<T[]>(key);

    if (existing == null) {
      existing = [];
    }

    const idx = existing.findIndex((entry) => isEqual(item, entry));

    if (idx === -1) {
      existing.push(item);
    } else {
      existing[idx] = item;
    }

    const result = await this.cache.setItem(key, [...existing]);
    this.subject$.next({ key, data: result });
    return result;
  }

  public async filterList<T>(
    key: string,
    filter: (entry: T) => boolean
  ): Promise<T[]> {
    const existing = await this.cache.getItem<T[]>(key);

    if (existing == null) {
      return [];
    }

    const filtered = existing.filter((entry) => filter(entry));
    const result = await this.cache.setItem(key, filtered);
    this.subject$.next({ key, data: result });
    return result;
  }
}

export { ObservableCache };
